import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { ActivityStatus } from 'types';

export interface TeamTaskUser {
  uuid: string;
  name: string;
  email?: string;
  avatar: string | null;
  last_activity?: string;
  activity_status?: ActivityStatus;
  resolvedStatus: ActivityStatus;
}

export interface TeamTask {
  uuid: string;
  task_title: string;
  task_status: string;
  task_priority?: number | string | null;
  task_end_date?: string | null;
  task_sort?: number | null;
  customer_business_name?: string | null;
  project_title?: string | null;
  request?: { uuid: string; subject: string } | null;
  related_users?: Array<{ uuid: string; name: string; avatar?: string | null; pivot?: { type: string } }>;
}

/** A company sector (development, marketing, ...) with its members. */
export interface TeamGroup {
  uuid: string;
  name: string;
  users: { uuid: string; name: string }[];
}

export const DEFAULT_ACTIVITY_STATUSES: ActivityStatus[] = ['online', 'away'];

/** What the user picked on the toolbar. Persisted, unlike the fetched data. */
export interface TeamTaskFilters {
  search: string;
  /** Member uuids and `__group__<uuid>` tokens, as chosen in the select. */
  memberSelection: string[];
  statusFilters: ActivityStatus[];
}

export interface TeamTaskActivityState {
  loading: boolean;
  isRevalidating: boolean;
  error: boolean;
  hasEverFetched: boolean;
  users: TeamTaskUser[];
  tasks: TeamTask[];
  groups: TeamGroup[];
  filters: TeamTaskFilters;
}

const initialFilters: TeamTaskFilters = {
  search: '',
  memberSelection: [],
  statusFilters: DEFAULT_ACTIVITY_STATUSES,
};

const initialState: TeamTaskActivityState = {
  loading: false,
  isRevalidating: false,
  error: false,
  hasEverFetched: false,
  users: [],
  tasks: [],
  groups: [],
  filters: initialFilters,
};

export const teamTaskActivitySlice = createSlice({
  name: 'teamTaskActivity',
  initialState,
  reducers: {
    fetchTeamTaskActivity: () => {},
    setTeamTaskActivityLoading: (state) => ({
      ...state,
      loading: true,
      error: false,
    }),
    setTeamTaskActivityRevalidating: (state) => ({
      ...state,
      isRevalidating: true,
      error: false,
    }),
    setTeamTaskActivitySuccess: (state, action) => ({
      ...state,
      loading: false,
      isRevalidating: false,
      error: false,
      hasEverFetched: true,
      users: action.payload.users,
      tasks: action.payload.tasks,
      groups: action.payload.groups,
    }),
    setTeamTaskActivityError: (state) => ({
      ...state,
      loading: false,
      isRevalidating: false,
      error: true,
    }),
    updateTeamTaskUserStatusFromWebSocket: (state, action) => {
      const { userId, activityStatus } = action.payload;

      const userIndex = state.users.findIndex((u) => u.uuid === userId);
      if (userIndex !== -1) {
        state.users[userIndex].activity_status = activityStatus;
        state.users[userIndex].resolvedStatus = activityStatus;
      }
    },
    setTeamTaskFilters: (state, action: PayloadAction<Partial<TeamTaskFilters>>) => ({
      ...state,
      filters: { ...state.filters, ...action.payload },
    }),
    resetTeamTaskFilters: (state) => ({
      ...state,
      filters: initialFilters,
    }),
    resetTeamTaskActivity: () => initialState,
  },
});

export const {
  fetchTeamTaskActivity,
  setTeamTaskActivityLoading,
  setTeamTaskActivityRevalidating,
  setTeamTaskActivitySuccess,
  setTeamTaskActivityError,
  updateTeamTaskUserStatusFromWebSocket,
  setTeamTaskFilters,
  resetTeamTaskFilters,
  resetTeamTaskActivity,
} = teamTaskActivitySlice.actions;

/**
 * Only the toolbar selection survives a reload, the same way the tasks list
 * keeps its filters. Users, tasks and groups are deliberately left out: they go
 * stale immediately and are refetched on mount.
 */
export default persistReducer(
  {
    key: teamTaskActivitySlice.name,
    storage,
    whitelist: ['filters'],
    version: 1,
  },
  teamTaskActivitySlice.reducer,
);
